import { useEffect } from 'react';
import type * as React from 'react';
import { Link } from 'react-router';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';

export interface Crumb {
  label: string;
  to?: string;
}

export interface PageProps {
  title: string;
  description?: string;
  breadcrumbs?: Crumb[];
  /** The one action this page exists to enable. At most one. */
  action?: React.ReactNode;
  children: React.ReactNode;
  /** Wide tables opt out of the reading-width cap. */
  width?: 'default' | 'wide';
}

/**
 * The page frame every screen uses: browser title, breadcrumbs, heading block,
 * one primary action, and a content column that stops widening long before a
 * 27-inch monitor turns a table into a horizon.
 */
export function Page({
  title,
  description,
  breadcrumbs,
  action,
  children,
  width = 'default',
}: PageProps): React.JSX.Element {
  useEffect(() => {
    document.title = `${title} · Baby Sesame Admin`;
  }, [title]);

  return (
    <div
      className={cn('mx-auto px-6 py-8', width === 'wide' ? 'max-w-[1600px]' : 'max-w-[1200px]')}
    >
      {breadcrumbs && breadcrumbs.length > 0 ? (
        <nav aria-label="Breadcrumb" className="mb-3">
          <ol className="flex flex-wrap items-center gap-1 text-xs text-ink-faint">
            {breadcrumbs.map((crumb, index) => (
              <li key={`${crumb.label}-${index}`} className="flex items-center gap-1">
                {index > 0 ? <ChevronRight className="size-3" aria-hidden="true" /> : null}
                {crumb.to ? (
                  <Link to={crumb.to} className="rounded transition-colors hover:text-ink">
                    {crumb.label}
                  </Link>
                ) : (
                  <span aria-current="page" className="text-ink-soft">
                    {crumb.label}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      ) : null}

      <header className="flex flex-wrap items-start justify-between gap-4 border-b border-line pb-5">
        <div className="min-w-0">
          <h1 className="text-xl font-semibold tracking-tight text-ink">{title}</h1>
          {description ? (
            <p className="mt-1.5 max-w-2xl text-sm text-ink-soft">{description}</p>
          ) : null}
        </div>
        {action ? <div className="flex shrink-0 items-center gap-2">{action}</div> : null}
      </header>

      <div className="pt-6">{children}</div>
    </div>
  );
}

/** A framed note for the warnings 06 attaches to specific screens. */
export function Notice({
  tone = 'caution',
  title,
  children,
}: {
  tone?: 'caution' | 'info';
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      className={cn(
        'rounded-card border px-4 py-3 text-sm',
        tone === 'caution'
          ? 'border-caution/30 bg-caution-wash text-caution'
          : 'border-info/30 bg-info-wash text-info',
      )}
    >
      <p className="font-semibold">{title}</p>
      <div className="mt-1 text-ink-soft">{children}</div>
    </div>
  );
}
